/**
 * W-15 会话列表页（story AC-4/AC-5）。单列居中；REST 拉取 + 五态 + 刷新按钮。
 * 发起会话:从商品详情「先聊一聊」/ 订单「联系对方」带 state{sellerId,seedProductId} 进来 →
 *   startConversation 建/取会话 → 跳 ChatPage。视觉真源 design/conversations.html。
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { listConversations, startConversation, type ConversationItem } from '@/api/im';
import { ServerError } from '@/api/client';
import { queryClient } from '@/lib/queryClient';
import { toast } from '@/components/ui/toast';
import { ConversationRow } from '@/components/im/ConversationRow';

export const CONVERSATIONS_QUERY_KEY = ['im', 'conversations'] as const;

interface StartState {
  peerUserId?: string;
  seedProductId?: string;
}

export function ConversationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const startState = (location.state ?? null) as StartState | null;
  const [starting, setStarting] = useState(!!startState?.peerUserId);
  const startedRef = useRef(false);

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: CONVERSATIONS_QUERY_KEY,
    queryFn: listConversations,
  });

  // 带发起参数进来 → 建/取会话 → 跳聊天页(只跑一次)。
  useEffect(() => {
    if (startedRef.current) return;
    const peerUserId = startState?.peerUserId;
    if (!peerUserId) return;
    startedRef.current = true;
    startConversation(peerUserId, startState?.seedProductId)
      .then((resp) => {
        if (!resp.conversationId) throw new Error('会话创建失败');
        // 刷新列表,让 ChatPage 能从缓存回填对端信息。
        void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
        navigate(`/chat/${resp.conversationId}`, {
          replace: true,
          state: { peerUserId, seedProductId: startState?.seedProductId },
        });
      })
      .catch((err) => {
        setStarting(false);
        startedRef.current = false;
        toast.error(err instanceof ServerError ? err.message || '打开会话失败' : '打开会话失败');
        // 清掉 state,避免返回时重复触发。
        navigate('/conversations', { replace: true });
      });
  }, [startState, navigate]);

  const items = data?.items ?? [];

  const openChat = (item: ConversationItem) => {
    navigate(`/chat/${item.conversationId}`, {
      state: {
        peerUserId: item.peerUserId,
        peerNickname: item.peerNickname,
        peerAvatar: item.peerAvatar,
        seedProductId: item.seedProductId,
      },
    });
  };

  return (
    <div className="inner section">
      <div className="container-read">
        <div className="row between mb-6">
          <h1 className="h2">消息</h1>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? '刷新中…' : '刷新'}
          </button>
        </div>

        {starting ? (
          <div className="state" data-testid="messages-starting">
            <div className="ic">💬</div>
            <p className="muted sm">正在打开会话…</p>
          </div>
        ) : isLoading ? (
          <div className="state" data-testid="messages-loading">
            <div className="ic">⏳</div>
            <p className="muted sm">加载中…</p>
          </div>
        ) : isError ? (
          <div className="state" data-testid="messages-error">
            <div className="ic">⚠️</div>
            {error instanceof ServerError && error.code === '010100' ? (
              <p className="sm" style={{ color: 'var(--color-destructive)' }}>
                请先登录
              </p>
            ) : (
              <button
                type="button"
                data-testid="messages-error-retry"
                className="btn btn-secondary"
                onClick={() => refetch()}
              >
                加载失败,点击重试
              </button>
            )}
          </div>
        ) : items.length === 0 ? (
          <div className="state" data-testid="messages-empty">
            <div className="ic">💬</div>
            <p className="muted sm">还没有消息</p>
            <p className="xs muted mt-2">在商品详情点「先聊一聊」或订单里「联系对方」即可发起会话。</p>
          </div>
        ) : (
          <section className="list">
            {items.map((item) => (
              <ConversationRow key={item.conversationId} item={item} onSelect={openChat} />
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
